import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./FootballGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "../Quiz/quizQueue.ts";
import { footballBestScoreKey, readBestScore, recordScore } from "../Quiz/bestScore.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { FootballGameRun, FootballResult } from "./FootballGameRun.tsx";
import { FootballGameSummary } from "./FootballGameSummary.tsx";
import { Defense, DEFENSES, RUSH_MS } from "./footballGame.ts";
import { footballGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface FootballGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: FootballResult;
    best: number | null;
    isNewBest: boolean;
}

/** Each defense's name. `labelKey` so StringKeys.test.js sees the keys, as QuizPage's modes do. */
const DEFENSE_LABELS: Record<Defense, { labelKey: StringKey }> = {
    rookie: { labelKey: "flashCards.footballDefenseRookie" },
    pro: { labelKey: "flashCards.footballDefensePro" },
    allPro: { labelKey: "flashCards.footballDefenseAllPro" },
};

/**
 * Math Facts Football (ADR 0103): a keyword landing page around a four-drive
 * game on the quiz's queue, marking and stores. A quiz route carrying
 * `game: "football"`; its `quiz` is only the operation the picker opens on.
 */
export function FootballGamePage({ slug, operation }: FootballGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [defense, setDefense] = useState<Defense | null>(null);
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);
    // Captured at kick-off, so a result is keyed by what was actually played.
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);

    const ready = canBuildDeck(settings);
    const playing = defense !== null && queue !== null && summary === null;
    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);

    function kickOff(chosen: Defense) {
        // Built here from settings as they stand, never during render: the
        // generator is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setPlayedOperations(operationsOf(settings));
        setDefense(chosen);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function changeDefense() {
        setDefense(null);
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: FootballResult) => {
            const key = footballBestScoreKey(playedOperations, result.defense);
            const previous = readBestScore(key);
            const best = recordScore(key, result.points);
            // A wrong throw and a sack are misses; a catch clears like a right
            // quiz answer (ADR 0103 §1, ADR 0012).
            result.misses.forEach((miss) => recordMiss(factKey(miss.front)));
            result.correctFronts.forEach((front) => recordCorrect(factKey(front)));
            setMissesVersion((v) => v + 1);
            // After the stores, as on the quiz: analytics never costs a best score.
            track("quiz_completed", footballGameEventParams(playedOperations, result));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest: previous !== null ? result.points > previous : result.points > 0,
            });
        },
        [playedOperations]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool">
                {summary ? (
                    <FootballGameSummary
                        result={summary.result}
                        best={summary.best}
                        isNewBest={summary.isNewBest}
                        onPlayAgain={() => kickOff(summary.result.defense)}
                        onChangeDefense={changeDefense}
                    />
                ) : playing ? (
                    <FootballGameRun key={runId} queue={queue!} defense={defense!} onFinish={handleFinish} />
                ) : (
                    <div className="quizModePicker footballIntro">
                        <p className="footballIntroHow">{t("flashCards.footballHowToPlay")}</p>
                        <p className="quizModePrompt">{t("flashCards.footballWhichFacts")}</p>
                        <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.footballWhichFacts")} />
                        <p className="quizModePrompt">{t("flashCards.footballChooseDefense")}</p>
                        {ready ? (
                            <div className="footballDefenses">
                                {DEFENSES.map((option) => (
                                    <button key={option} type="button" onClick={() => kickOff(option)}>
                                        <span className="footballDefenseName">{t(DEFENSE_LABELS[option].labelKey)}</span>
                                        <span className="footballDefenseTime">
                                            {format(t("flashCards.footballDefenseSecondsTemplate"), {
                                                n: RUSH_MS[option] / 1000,
                                            })}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                        )}
                    </div>
                )}

                {!playing && plainQuiz && (
                    <p className="footballCalmer">
                        {t("flashCards.gameCalmerPrefix")}
                        <a href={plainQuiz.slug}>{t("flashCards.gameCalmerLink")}</a>
                        {t("flashCards.gameCalmerSuffix")}
                    </p>
                )}

                {!playing && <MissedFactsPractice refreshKey={missesVersion} />}

                {!playing && (
                    <details className="quizSettings">
                        <summary>{t("flashCards.changeWhatToPractise")}</summary>
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default FootballGamePage;
